/**
 * Request Manager Utility
 * 
 * Issue #12: No Loading State Management
 * 
 * Provides utilities for managing API requests with:
 * - Loading state checks to prevent duplicate simultaneous requests
 * - AbortController for request cancellation
 * - Cleanup on component unmount
 * 
 * @module requestManager
 */

/**
 * Hook to manage request state and AbortController
 * 
 * Usage:
 * ```typescript
 * const requestManager = useRequestManager();
 * 
 * const fetchData = async () => {
 *   if (!requestManager.canMakeRequest()) return;
 *   
 *   try {
 *     requestManager.startRequest();
 *     const data = await apiCall();
 *     // Process data
 *   } catch (error) {
 *     if (requestManager.isAborted(error)) return;
 *     // Handle error
 *   } finally {
 *     requestManager.endRequest();
 *   }
 * };
 * 
 * useEffect(() => {
 *   return () => requestManager.cleanup();
 * }, []);
 * ```
 */

import { useRef, useCallback } from 'react';

export interface RequestManager {
  /**
   * Check if a new request can be made (not currently loading)
   */
  canMakeRequest: () => boolean;
  
  /**
   * Mark request as started and create new AbortController
   */
  startRequest: () => void;
  
  /**
   * Mark request as ended
   */
  endRequest: () => void;
  
  /**
   * Get the current AbortController signal
   */
  getSignal: () => AbortSignal | undefined;
  
  /**
   * Abort the current request
   */
  abort: () => void;
  
  /**
   * Check if an error is an abort error
   */
  isAborted: (error: unknown) => boolean;
  
  /**
   * Cleanup - abort pending requests
   */
  cleanup: () => void;
  
  /**
   * Check if currently loading
   */
  isLoading: () => boolean;
}

export const useRequestManager = (): RequestManager => {
  const isLoadingRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const canMakeRequest = useCallback((): boolean => {
    return !isLoadingRef.current;
  }, []);

  const startRequest = useCallback((): void => {
    isLoadingRef.current = true;
    
    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController
    abortControllerRef.current = new AbortController();
  }, []);

  const endRequest = useCallback((): void => {
    isLoadingRef.current = false;
  }, []);

  const getSignal = useCallback((): AbortSignal | undefined => {
    return abortControllerRef.current?.signal;
  }, []);

  const abort = useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const isAborted = useCallback((error: unknown): boolean => {
    return (error instanceof Error) && (error.name === 'AbortError' || error.message.includes('aborted'));
  }, []);

  const cleanup = useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    isLoadingRef.current = false;
  }, []);

  const isLoading = useCallback((): boolean => {
    return isLoadingRef.current;
  }, []);

  return {
    canMakeRequest,
    startRequest,
    endRequest,
    getSignal,
    abort,
    isAborted,
    cleanup,
    isLoading,
  };
};

/**
 * Simple loading state guard
 * 
 * Usage:
 * ```typescript
 * const handleClick = async () => {
 *   if (isLoading) return; // Guard against duplicate requests
 *   
 *   setIsLoading(true);
 *   try {
 *     await apiCall();
 *   } finally {
 *     setIsLoading(false);
 *   }
 * };
 * ```
 */
export const createLoadingGuard = (isLoading: boolean): boolean => {
  return isLoading;
};

/**
 * Wrapper for async functions with loading state management
 * 
 * Usage:
 * ```typescript
 * const handleClick = withLoadingState(
 *   isLoading,
 *   setIsLoading,
 *   async () => {
 *     await apiCall();
 *   }
 * );
 * ```
 */
export const withLoadingState = <T extends unknown[]>(
  isLoading: boolean,
  setIsLoading: (loading: boolean) => void,
  fn: (...args: T) => Promise<void>
) => {
  return async (...args: T): Promise<void> => {
    // Issue #12: Prevent duplicate simultaneous requests
    if (isLoading) {
      console.warn('[RequestManager] Request already in progress, ignoring duplicate request');
      return;
    }

    setIsLoading(true);
    try {
      await fn(...args);
    } finally {
      setIsLoading(false);
    }
  };
};

/**
 * Create an AbortController with automatic cleanup
 * 
 * Usage:
 * ```typescript
 * const abortControllerRef = useRef<AbortController | null>(null);
 * 
 * const fetchData = async () => {
 *   abortControllerRef.current = createAbortController();
 *   
 *   try {
 *     const response = await fetch(url, {
 *       signal: abortControllerRef.current.signal
 *     });
 *   } catch (error) {
 *     if (isAbortError(error)) return;
 *     // Handle error
 *   }
 * };
 * 
 * useEffect(() => {
 *   return () => cleanupAbortController(abortControllerRef.current);
 * }, []);
 * ```
 */
export const createAbortController = (): AbortController => {
  return new AbortController();
};

/**
 * Cleanup an AbortController
 */
export const cleanupAbortController = (controller: AbortController | null): void => {
  if (controller) {
    controller.abort();
  }
};

/**
 * Check if an error is an abort error
 */
export const isAbortError = (error: unknown): boolean => {
  return (error instanceof Error) && (error.name === 'AbortError' || error.message.includes('aborted'));
};

/**
 * Standard pattern for API calls with loading state and abort controller
 * 
 * Usage:
 * ```typescript
 * const [isLoading, setIsLoading] = useState(false);
 * const abortControllerRef = useRef<AbortController | null>(null);
 * 
 * const fetchData = async () => {
 *   // Issue #12: Prevent duplicate simultaneous requests
 *   if (isLoading) return;
 *   
 *   setIsLoading(true);
 *   abortControllerRef.current = createAbortController();
 *   
 *   try {
 *     const response = await fetch(url, {
 *       signal: abortControllerRef.current.signal
 *     });
 *     // Process response
 *   } catch (error) {
 *     if (isAbortError(error)) return; // Request was cancelled
 *     // Handle error
 *   } finally {
 *     setIsLoading(false);
 *   }
 * };
 * 
 * useEffect(() => {
 *   return () => cleanupAbortController(abortControllerRef.current);
 * }, []);
 * ```
 */

